import { memo, useState } from 'react';
import { useCategories } from '@/hooks/useCategories';
import { useCatalogFilters } from '@/hooks/useCatalogFilters';
import { cn } from '@/lib/utils';
import { Loader2, X, ChevronDown, Search } from 'lucide-react';
import { Input } from '@/components/ui';

type CatalogFilterState = {
    categories?: string[];
    ageRanges?: string[];
    brands?: string[];
    query?: string;
    isAvailable?: boolean;
};

interface CatalogFiltersProps {
    filters: CatalogFilterState;
    onChange: (filters: CatalogFilterState) => void;
    onSearchChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    locationId?: string;
}

const FilterSection = ({
    title,
    isLoading,
    loadingText = 'Loading...',
    children,
    defaultOpen = true,
}: {
    title: string;
    isLoading?: boolean;
    loadingText?: string;
    children: React.ReactNode;
    defaultOpen?: boolean;
}) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <section>
            <button
                type="button"
                onClick={() => setIsOpen((v) => !v)}
                className="flex w-full items-center justify-between gap-2 pb-3 text-left"
                aria-expanded={isOpen}
            >
                <h3 className="font-display text-lg text-foreground">{title}</h3>
                <ChevronDown
                    className={cn(
                        'h-4 w-4 flex-shrink-0 text-muted-foreground/60 transition-transform duration-200',
                        isOpen && 'rotate-180'
                    )}
                    aria-hidden="true"
                />
            </button>

            {/* Collapsible body — grid-rows trick avoids animating height */}
            <div
                className={cn(
                    'grid transition-[grid-template-rows] duration-200',
                    isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                )}
            >
                <div className="overflow-hidden">
                    {isLoading ? (
                        <div className="flex items-center gap-2 pb-4 pt-1 text-sm text-muted-foreground">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            {loadingText}
                        </div>
                    ) : (
                        <div className="space-y-2 pb-4">{children}</div>
                    )}
                </div>
            </div>
        </section>
    );
};

const FilterCheckbox = ({
    id,
    name,
    label,
    count,
    checked,
    onChange,
}: {
    id: string;
    name: string;
    label: string;
    count?: number;
    checked: boolean;
    onChange: () => void;
}) => (
    <label
        htmlFor={id}
        className={cn(
            'group flex cursor-pointer items-center justify-between rounded-2xl border px-3 py-3 transition-colors select-none',
            checked
                ? 'border-primary/20 bg-primary/10'
                : 'border-transparent bg-secondary/60 hover:border-border hover:bg-secondary'
        )}
    >
        <div className="flex items-center gap-3">
            <div
                className={cn(
                    'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border transition-all',
                    checked
                        ? 'border-primary bg-primary'
                        : 'border-border bg-background group-hover:border-primary/40'
                )}
                aria-hidden="true"
            >
                {checked && (
                    <svg className="h-2.5 w-2.5 text-white" fill="none" viewBox="0 0 10 10">
                        <path d="M1.5 5l2.5 2.5 4.5-4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                )}
            </div>
            <input
                id={id}
                name={name}
                type="checkbox"
                checked={checked}
                onChange={onChange}
                className="sr-only"
                aria-label={label}
            />
            <span className={cn(
                'text-sm transition-colors',
                checked ? 'font-medium text-foreground' : 'text-muted-foreground'
            )}>
                {label}
            </span>
        </div>
        {count !== undefined && (
            <span
                className={cn(
                    'rounded-full px-2 py-1 text-[11px] font-bold',
                    checked ? 'bg-white/80 text-primary' : 'bg-background text-muted-foreground'
                )}
                aria-hidden="true"
            >
                {count}
            </span>
        )}
    </label>
);

export const CatalogFilters = memo(({ filters, onChange, onSearchChange, locationId }: CatalogFiltersProps) => {
    const { data: categoriesData, isLoading: isCategoriesLoading } = useCategories();
    const { data: filtersData, isLoading: isFiltersLoading } = useCatalogFilters();

    const handleCategoryChange = (categorySlug: string) => {
        const currentCategories = filters.categories || [];
        const isSelected = currentCategories.includes(categorySlug);
        const newCategories = isSelected
            ? currentCategories.filter(c => c !== categorySlug)
            : [...currentCategories, categorySlug];
        onChange({ ...filters, categories: newCategories.length > 0 ? newCategories : undefined });
    };

    const handleAgeChange = (age: string) => {
        const currentAgeRanges = filters.ageRanges || [];
        const isSelected = currentAgeRanges.includes(age);
        const newAgeRanges = isSelected
            ? currentAgeRanges.filter(a => a !== age)
            : [...currentAgeRanges, age];
        onChange({ ...filters, ageRanges: newAgeRanges.length > 0 ? newAgeRanges : undefined });
    };

    const handleBrandChange = (brand: string) => {
        const currentBrands = filters.brands || [];
        const isSelected = currentBrands.includes(brand);
        const newBrands = isSelected
            ? currentBrands.filter(b => b !== brand)
            : [...currentBrands, brand];
        onChange({ ...filters, brands: newBrands.length > 0 ? newBrands : undefined });
    };

    const hasActiveFilters =
        (filters.categories?.length ?? 0) > 0 ||
        (filters.ageRanges?.length ?? 0) > 0 ||
        (filters.brands?.length ?? 0) > 0 ||
        filters.isAvailable === true;

    const clearAll = () => onChange({ categories: undefined, ageRanges: undefined, brands: undefined, isAvailable: undefined });

    return (
        <div className="space-y-6">
            {/* Search — only rendered when onSearchChange is provided (sidebar context) */}
            {onSearchChange && (
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                    <Input
                        placeholder="Search products..."
                        className="h-10 w-full rounded-full border-border/80 bg-background pl-10 text-sm"
                        value={filters.query ?? ''}
                        onChange={onSearchChange}
                        aria-label="Search products in catalogue"
                    />
                </div>
            )}

            <div className="flex items-center justify-between gap-3">
                <h2 className="font-display text-xl text-foreground">Filters</h2>
                {hasActiveFilters && (
                    <button
                        onClick={clearAll}
                        className="inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:bg-secondary/80 hover:text-foreground"
                        aria-label="Clear all filters"
                    >
                        <X className="h-3 w-3" />
                        Clear all
                    </button>
                )}
            </div>

            <div className="divide-y divide-border/50">
                <FilterSection title="Category" isLoading={isCategoriesLoading} loadingText="Loading categories...">
                    {categoriesData && categoriesData.filter((c) => (c.equipmentCount ?? 0) > 0).length > 0 ? (
                        categoriesData
                            .filter((c) => (c.equipmentCount ?? 0) > 0)
                            .map((category) => (
                                <FilterCheckbox
                                    key={category.id}
                                    id={`category-${category.slug || category.id}`}
                                    name="category"
                                    label={category.name}
                                    count={category.equipmentCount}
                                    checked={(filters.categories || []).includes(category.slug || category.id)}
                                    onChange={() => handleCategoryChange(category.slug || category.id)}
                                />
                            ))
                    ) : !isCategoriesLoading ? (
                        <p className="pb-4 text-sm text-muted-foreground">No categories available</p>
                    ) : null}
                </FilterSection>

                <FilterSection title="Age Range" isLoading={isFiltersLoading}>
                    {filtersData?.ageRanges && filtersData.ageRanges.filter((a) => (a.count ?? 0) > 0).length > 0 ? (
                        filtersData.ageRanges
                            .filter((a) => (a.count ?? 0) > 0)
                            .map((age) => (
                                <FilterCheckbox
                                    key={age.id}
                                    id={`age-${age.id}`}
                                    name="age"
                                    label={age.label}
                                    count={age.count}
                                    checked={(filters.ageRanges || []).includes(age.id)}
                                    onChange={() => handleAgeChange(age.id)}
                                />
                            ))
                    ) : !isFiltersLoading ? (
                        <p className="pb-4 text-sm text-muted-foreground">No age ranges available</p>
                    ) : null}
                </FilterSection>

                <FilterSection title="Brand" isLoading={isFiltersLoading}>
                    {filtersData?.brands && filtersData.brands.filter((b) => (b.count ?? 0) > 0).length > 0 ? (
                        filtersData.brands
                            .filter((b) => (b.count ?? 0) > 0)
                            .map((brand) => (
                                <FilterCheckbox
                                    key={brand.id}
                                    id={`brand-${brand.id}`}
                                    name="brand"
                                    label={brand.label}
                                    count={brand.count}
                                    checked={(filters.brands || []).includes(brand.id)}
                                    onChange={() => handleBrandChange(brand.id)}
                                />
                            ))
                    ) : !isFiltersLoading ? (
                        <p className="pb-4 text-sm text-muted-foreground">No brands available</p>
                    ) : null}
                </FilterSection>

                {locationId && (
                    <FilterSection title="Availability" defaultOpen={true}>
                        <label
                            className={cn(
                                'group flex cursor-pointer items-center justify-between rounded-2xl border px-3 py-3 transition-colors select-none',
                                filters.isAvailable
                                    ? 'border-primary/20 bg-primary/10'
                                    : 'border-transparent bg-secondary/60 hover:border-border hover:bg-secondary'
                            )}
                        >
                            <div className="flex items-center gap-3">
                                <div
                                    className={cn(
                                        'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border transition-all',
                                        filters.isAvailable
                                            ? 'border-primary bg-primary'
                                            : 'border-border bg-background group-hover:border-primary/40'
                                    )}
                                    aria-hidden="true"
                                >
                                    {filters.isAvailable && (
                                        <svg className="h-2.5 w-2.5 text-white" fill="none" viewBox="0 0 10 10">
                                            <path d="M1.5 5l2.5 2.5 4.5-4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    )}
                                </div>
                                <input
                                    type="checkbox"
                                    checked={filters.isAvailable ?? false}
                                    onChange={() => onChange({ ...filters, isAvailable: filters.isAvailable ? undefined : true })}
                                    className="sr-only"
                                    aria-label="Show available items only"
                                />
                                <span className={cn(
                                    'text-sm transition-colors',
                                    filters.isAvailable ? 'font-medium text-foreground' : 'text-muted-foreground'
                                )}>
                                    Available only
                                </span>
                            </div>
                        </label>
                    </FilterSection>
                )}
            </div>
        </div>
    );
});

CatalogFilters.displayName = 'CatalogFilters';
